import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import localizedStrings from 'localization';
import {
  FieldErrorMessage,
  PublishShowcaseAppletsContainer,
  PublishShowcaseDescription,
  PublishShowcaseSelectAll,
  QuestSectionTitle,
} from 'modules/profile/components';
import { selectQuestPortfolioApplets } from 'modules/applets/selectors';
import AppletPreview from 'modules/profile/portfolio/quest-applets/AppletPreview';
import { SetFieldValueType, StepPropsSplit } from './typings';
import { buildQuestAppletsList } from 'helpers/AppletHelpers';
import useHandleAppletSelect from 'hooks/useHandleAppletSelect';
import useIsAppletSelected from 'hooks/useIsAppletSelected';
import CollapsibleOutlined from 'components/collapsible-outlined';

const SelectArtifacts = ({
  values,
  setFieldValue,
}: StepPropsSplit) => {
  const questPortfolioApplets = useSelector(selectQuestPortfolioApplets);

  const applets = useMemo(
    () =>
      buildQuestAppletsList(questPortfolioApplets),
    [questPortfolioApplets],
  );

  const selectAll = useCallback(
    (setFieldValue: SetFieldValueType) => {
      const allIds = applets
        .map((app) =>
          app.appletType === 'group-applet'
            ? app.applets?.map((groupApp) => groupApp.id)
            : app.id,
        )
        .flat();
      setFieldValue('applets', allIds);
    },
    [applets],
  );

  const hasSelectedAll = useCallback(
    () =>
      values?.applets?.length ===
      applets
        .map((app) =>
          app.appletType === 'group-applet'
            ? app.applets?.map((groupApp) => groupApp.id)
            : app.id,
        )
        .flat().length,
    [applets, values?.applets],
  );

  const deselectAll = useCallback((setFieldValue: SetFieldValueType) => {
    setFieldValue('applets', []);
  }, []);

  const handleSelectDeselect = useCallback(() => {
    if (hasSelectedAll()) {
      deselectAll(setFieldValue);
    } else {
      selectAll(setFieldValue);
    }
  }, [deselectAll, hasSelectedAll, selectAll, setFieldValue]);

  const handleAppletSelect = useHandleAppletSelect(
    setFieldValue,
    values?.applets || [],
    'applets',
  );

  const isSelected = useIsAppletSelected(values?.applets || []);

  return (
    <>
      <CollapsibleOutlined
        dataTestid="CollapsibleOutlined-SelectArtifacts"
        title={<QuestSectionTitle>{localizedStrings.artifactsIncluded}</QuestSectionTitle>}
        initOpen={false}
        childrenSelected={<>
          {values?.applets?.length && <PublishShowcaseAppletsContainer>
            {applets
              .filter(isSelected)
              .map((applet) =>
                <AppletPreview
                  key={applet.id}
                  applet={applet}
                  onAppletClick={() => { }}
                  showTitle={true}
                  height="168px"
                  width="178px"
                />)}
          </PublishShowcaseAppletsContainer>}
        </>}
      >
        <PublishShowcaseDescription>
          {values?.applets && values?.applets?.length < 3 ? (
            <FieldErrorMessage>
              {localizedStrings.showcaseArtifactsMinimumError}
            </FieldErrorMessage>
          ) : localizedStrings.selectShowcaseArtifacts}
        </PublishShowcaseDescription>

        <>
          <PublishShowcaseSelectAll
            type="button"
            onClick={handleSelectDeselect}>
            {hasSelectedAll()
              ? localizedStrings.deselectAll
              : localizedStrings.selectAll}
          </PublishShowcaseSelectAll>
          <PublishShowcaseAppletsContainer>
            {
              applets.map((applet) => (
                <AppletPreview
                  key={applet.id}
                  applet={applet}
                  onAppletClick={handleAppletSelect}
                  showTitle={true}
                  height="168px"
                  width="178px"
                  selected={isSelected(applet)}
                />
              ))
            }
          </PublishShowcaseAppletsContainer>
        </>
      </CollapsibleOutlined>
    </>
  )
};

export default SelectArtifacts;
